import {
  Award,
  BarChart3,
  DollarSign,
  Download,
  Eye,
  Info,
  MapPin,
  MousePointer2,
  Phone,
  Search,
  Target,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  PageLayout,
  SectionHeader,
  Tooltip,
} from "../components";
import { AdsAnalyticsSkeleton } from "../components/skeletons";
import { useAuth } from "../contexts/AuthContext";
import apiService from "../shared/apiService";
import { showToastError, showToastSuccess } from "../shared/alertService";
import { cn } from "../utils/cn";
import { getPlatformName, PlatformIcon } from "../utils/platformIcons";
import { useActiveContext } from "../contexts/ActiveContextContext";
import { useTranslation } from "../contexts/LanguageContext";
import { LinkedInDemographics } from "../components/dashboards/LinkedInDemographics";

const fmt = (n: number) => n.toLocaleString("pt-BR");

type PeriodKey = "7d" | "30d" | "90d" | "365d" | "all";

interface GmbMetrics {
  impressions: number;
  search_impressions: number;
  maps_impressions: number;
  direction_requests: number;
  website_clicks: number;
  phone_calls: number;
  total_clicks: number;
  locations_count: number;
}

interface MetaPlatformBreakdown {
  platform: string; // "facebook" | "instagram" | "unknown" | ... (vem direto do publisher_platform da Meta)
  spent: number;
  impressions: number;
  clicks: number;
  conversions: number;
  form_leads?: number;
}

interface CampaignPerformance {
  id: string;
  name: string;
  // "meta" é uma campanha única rodando em Facebook + Instagram ao mesmo
  // tempo (Advantage+ placements) — o detalhamento real por canal vem em
  // `metrics_breakdown`, não dá pra saber só pelo campo `platform`.
  platform: "facebook" | "instagram" | "meta" | "google_ads" | "linkedin";
  status: string;
  spent: number;
  impressions: number;
  clicks: number;
  conversions: number;
  cpc: number;
  ctr: number;
  roi: number;
  cost_per_lead: number;
  metrics_breakdown?: MetaPlatformBreakdown[];
}

const AdsAnalytics: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState<CampaignPerformance[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<PeriodKey>("7d");
  const { activeContext } = useActiveContext();
  const contextId = `${activeContext.type}-${activeContext.organization?.id || ""}-${activeContext.clientCompany?.id || ""}`;
  const [lastContextId, setLastContextId] = useState(contextId);

  const PERIODS = useMemo(
    () => [
      { key: "7d" as PeriodKey, label: t("7 dias"), days: 7 },
      { key: "30d" as PeriodKey, label: t("30 dias"), days: 30 },
      { key: "90d" as PeriodKey, label: t("90 dias"), days: 90 },
      { key: "365d" as PeriodKey, label: t("1 ano"), days: 365 },
      { key: "all" as PeriodKey, label: t("Todo período"), days: null },
    ],
    [t],
  );

  if (contextId !== lastContextId) {
    setCampaigns([]);
    setLoading(true);
    setLastContextId(contextId);
  }

  const selectedPeriod = PERIODS.find((p) => p.key === period)!;

  const fetchAnalytics = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      // `days` sozinho só filtra QUAIS campanhas entram na lista (por data de
      // criação) — não recalcula gasto/impressões/cliques DENTRO do período.
      // O backend só recalcula as métricas por período quando recebe
      // `date_preset` (+ start_date/end_date pra "custom"); sem isso, 7/30/90
      // dias e "Todo período" mostravam exatamente os mesmos números
      // (o acumulado total da campanha), fazendo o seletor de período parecer
      // quebrado. "Todo período" continua sem enviar nada (mostra o total
      // mesmo, que é o comportamento certo pra esse caso).
      let params: Record<string, string | number> = {};
      if (selectedPeriod.days) {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - selectedPeriod.days);
        const toYmd = (d: Date) => d.toISOString().split("T")[0];
        params = {
          days: selectedPeriod.days,
          date_preset: "custom",
          start_date: toYmd(start),
          end_date: toYmd(end),
        };
      }
      const performanceData = await apiService.get(
        `/api/ads/performance/compare/${user.id}`,
        params,
      );
      if (performanceData && performanceData.campaigns) {
        setCampaigns(performanceData.campaigns);
      }
    } catch (err) {
      console.error("Erro ao buscar analytics:", err);
      showToastError(t("Erro ao carregar analytics de anúncios"));
    } finally {
      setLoading(false);
    }
  }, [user?.id, contextId, selectedPeriod.days, t]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Importa campanhas do Google Ads que já existiam na conta antes de o
  // cliente ser integrado na plataforma (criadas direto no Google Ads, sem
  // nunca passar pelo wizard daqui) — sem isso, elas nunca entram no
  // pipeline de sincronização e nunca aparecem neste dashboard.
  const [importingGoogleAds, setImportingGoogleAds] = useState(false);
  const handleImportExistingGoogleAds = useCallback(async () => {
    setImportingGoogleAds(true);
    try {
      const data = await apiService.post(
        "/api/ads/google/campaigns/import-existing",
        {},
      );
      if (data?.success) {
        if (data.imported_count > 0) {
          showToastSuccess(
            t("{count} campanha(s) do Google Ads importada(s) com sucesso!", {
              count: data.imported_count,
            }),
          );
          fetchAnalytics();
        } else {
          showToastSuccess(
            t(
              "Nenhuma campanha nova encontrada — todas já estavam importadas.",
            ),
          );
        }
      } else {
        showToastError(data?.error || t("Erro ao importar campanhas."));
      }
    } catch (err: any) {
      showToastError(
        err?.message || t("Erro ao importar campanhas do Google Ads."),
      );
    } finally {
      setImportingGoogleAds(false);
    }
  }, [t, fetchAnalytics]);

  const [gmbData, setGmbData] = useState<GmbMetrics | null>(null);
  const [gmbLoading, setGmbLoading] = useState(true);

  const loadGmb = useCallback(() => {
    setGmbData(null);
    setGmbLoading(true);
    const days = selectedPeriod.days ?? 540;
    apiService
      .get<any>("/api/integrations/google/performance", { days })
      .then((r) => {
        if (r?.success) setGmbData(r as GmbMetrics);
      })
      .catch(() => {})
      .finally(() => setGmbLoading(false));
  }, [selectedPeriod.days]);

  useEffect(() => {
    loadGmb();
  }, [loadGmb]);

  // Métricas Agregadas
  const stats = useMemo(() => {
    const total = campaigns.length;
    const totalSpent = campaigns.reduce((acc, c) => acc + (c.spent || 0), 0);
    const totalLeads = campaigns.reduce(
      (acc, c) => acc + (c.conversions || 0),
      0,
    );
    const totalClicks = campaigns.reduce((acc, c) => acc + (c.clicks || 0), 0);
    const totalImpressions = campaigns.reduce(
      (acc, c) => acc + (c.impressions || 0),
      0,
    );

    const avgRoi =
      total > 0
        ? campaigns.reduce((acc, c) => acc + (c.roi || 0), 0) / total
        : 0;
    const avgCpa = totalLeads > 0 ? totalSpent / totalLeads : 0;
    const avgCtr =
      totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;

    // Dados por Plataforma
    // Campanhas "meta" (Advantage+ / "Ambos") rodam em Facebook E Instagram ao
    // mesmo tempo — o campo `platform` sozinho não diz como o gasto se divide
    // entre os dois. Usamos `metrics_breakdown` (vem da própria Meta, por
    // publisher_platform) pra atribuir os números reais a cada canal; só cai
    // pro fallback de "conta inteira nos dois" quando ainda não há breakdown
    // sincronizado (campanha muito nova).
    const metaChannelStats = (target: "facebook" | "instagram") => {
      let count = 0;
      let spent = 0;
      let leads = 0;
      let impressions = 0;
      let clicks = 0;
      let roiSum = 0;
      let roiCount = 0;

      for (const c of campaigns) {
        if (c.platform === target) {
          count += 1;
          spent += c.spent || 0;
          leads += c.conversions || 0;
          impressions += c.impressions || 0;
          clicks += c.clicks || 0;
          roiSum += c.roi || 0;
          roiCount += 1;
          continue;
        }
        if (c.platform === "meta") {
          const breakdown = c.metrics_breakdown?.find(
            (b) => b.platform?.toLowerCase() === target,
          );
          if (breakdown) {
            count += 1;
            spent += breakdown.spent || 0;
            leads += breakdown.conversions || 0;
            impressions += breakdown.impressions || 0;
            clicks += breakdown.clicks || 0;
            roiSum += c.roi || 0;
            roiCount += 1;
          } else if (!c.metrics_breakdown || c.metrics_breakdown.length === 0) {
            // Ainda sem breakdown sincronizado — melhor mostrar o valor
            // agregado nos dois canais do que esconder a campanha inteira.
            count += 1;
            spent += c.spent || 0;
            leads += c.conversions || 0;
            impressions += c.impressions || 0;
            clicks += c.clicks || 0;
            roiSum += c.roi || 0;
            roiCount += 1;
          }
        }
      }

      return {
        count,
        spent,
        leads,
        impressions,
        clicks,
        roi: roiCount > 0 ? roiSum / roiCount : 0,
      };
    };

    const googleCampaigns = campaigns.filter(
      (c) => c.platform === "google_ads",
    );
    const linkedinCampaigns = campaigns.filter(
      (c) => c.platform === "linkedin",
    );

    const platformStats = (list: typeof campaigns) => ({
      count: list.length,
      spent: list.reduce((a, c) => a + (c.spent || 0), 0),
      leads: list.reduce((a, c) => a + (c.conversions || 0), 0),
      impressions: list.reduce((a, c) => a + (c.impressions || 0), 0),
      clicks: list.reduce((a, c) => a + (c.clicks || 0), 0),
      roi:
        list.length > 0
          ? list.reduce((acc, c) => acc + (c.roi || 0), 0) / list.length
          : 0,
    });

    return {
      total,
      totalSpent,
      totalLeads,
      totalClicks,
      totalImpressions,
      avgRoi,
      avgCpa,
      avgCtr,
      facebook: metaChannelStats("facebook"),
      instagram: metaChannelStats("instagram"),
      google: platformStats(googleCampaigns),
      linkedin: platformStats(linkedinCampaigns),
    };
  }, [campaigns]);

  const linkedinCampaignsList = useMemo(
    () => campaigns.filter((c) => c.platform === "linkedin" && c.id),
    [campaigns],
  );

  const [selectedLinkedInCampId, setSelectedLinkedInCampId] =
    useState<string>("");

  useEffect(() => {
    if (linkedinCampaignsList.length > 0 && !selectedLinkedInCampId) {
      setSelectedLinkedInCampId(linkedinCampaignsList[0].id);
    }
  }, [linkedinCampaignsList, selectedLinkedInCampId]);

  const selectedLinkedInCamp = useMemo(
    () =>
      linkedinCampaignsList.find((c) => c.id === selectedLinkedInCampId) ||
      linkedinCampaignsList[0],
    [linkedinCampaignsList, selectedLinkedInCampId],
  );

  const KPI_COLORS = {
    indigo: {
      border: "border-indigo-200",
      text: "text-indigo-700",
      iconBg: "bg-indigo-100",
      iconText: "text-indigo-600",
      hoverGlow: "hover:shadow-[0_4px_20px_rgba(99,102,241,0.14)]",
      subBg: "bg-indigo-50/70",
      subText: "text-indigo-600",
    },
    purple: {
      border: "border-purple-200",
      text: "text-purple-700",
      iconBg: "bg-purple-100",
      iconText: "text-purple-600",
      hoverGlow: "hover:shadow-[0_4px_20px_rgba(168,85,247,0.14)]",
      subBg: "bg-purple-50/70",
      subText: "text-purple-600",
    },
    emerald: {
      border: "border-emerald-200",
      text: "text-emerald-700",
      iconBg: "bg-emerald-100",
      iconText: "text-emerald-600",
      hoverGlow: "hover:shadow-[0_4px_20px_rgba(16,185,129,0.14)]",
      subBg: "bg-emerald-50/70",
      subText: "text-emerald-600",
    },
    amber: {
      border: "border-amber-200",
      text: "text-amber-700",
      iconBg: "bg-amber-100",
      iconText: "text-amber-600",
      hoverGlow: "hover:shadow-[0_4px_20px_rgba(245,158,11,0.14)]",
      subBg: "bg-amber-50/70",
      subText: "text-amber-600",
    },
  };

  const overviewKPIs = [
    {
      title: t("Investimento"),
      value: new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(stats.totalSpent),
      icon: DollarSign,
      color: "indigo" as const,
      sub: t("Gasto total no período"),
    },
    {
      title: t("Conversões (Leads)"),
      value: fmt(stats.totalLeads),
      icon: Users,
      color: "purple" as const,
      sub: t("Total de conversões registradas"),
    },
    {
      title: t("CPA Médio"),
      value: new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(stats.avgCpa),
      icon: Target,
      color: "emerald" as const,
      sub: t("Custo médio por aquisição"),
      tooltip: t(
        "Total investido dividido pelo número de conversões (leads) obtidas no período selecionado.",
      ),
    },
    {
      title: t("ROI Geral"),
      value: `${stats.avgRoi.toFixed(1)}x`,
      icon: Zap,
      color: "amber" as const,
      sub: t("Retorno médio geral"),
      tooltip: t(
        "Média do retorno sobre o investimento (ROI) registrado em todas as campanhas ativas.",
      ),
    },
  ];

  return (
    <PageLayout
      title={t("Insights de Anúncios")}
      subtitle={t(
        "Métricas consolidadas de investimento, conversão, CPA e ROI por plataforma e campanha.",
      )}
      containerSize="full"
      showContainer={true}
      padding="none"
      className="w-full px-2! py-2!"
      loading={loading}
      skeleton={<AdsAnalyticsSkeleton />}
      actions={
        // Fica na description bar (fora da área com scroll) porque é um
        // status passivo da página inteira, não um controle de uma seção
        // específica — precisa continuar visível mesmo com a página rolada.
        // Mesmo padrão de PostsAnalytics.tsx.
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleImportExistingGoogleAds}
            disabled={importingGoogleAds}
            className="!min-h-0 h-8 px-2.5 bg-white hover:bg-purple-50 border-purple-200 text-purple-700 font-bold text-[10px] uppercase tracking-wider rounded-lg shadow-none"
            icon={
              <Download
                className={cn(
                  "w-3.5 h-3.5",
                  importingGoogleAds && "animate-bounce",
                )}
              />
            }
            title={t(
              "Busca campanhas do Google Ads já existentes na conta conectada (criadas fora da plataforma) e importa pro dashboard.",
            )}
          >
            {importingGoogleAds
              ? t("Importando...")
              : t("Importar Campanhas Google Ads")}
          </Button>
          <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-green-50 border border-green-200">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-[10px] font-black text-green-700 uppercase tracking-wider">
              {t("Monitoramento Ativo")}
            </span>
          </div>
        </div>
      }
    >
      <div className="w-full flex flex-col gap-2">
        {/* ========================================================================= */}
        {/* BANDA 1: Header de Controle & Visão Geral (4 KPIs)                         */}
        {/* ========================================================================= */}
        <div className="shrink-0 flex flex-col bg-white rounded-xl border border-purple-200 p-3 shadow-xs">
          <SectionHeader
            icon={<TrendingUp className="h-5 w-5" />}
            title={t("Métricas de Performance")}
            subtitle={t("Visão Geral de Mídia")}
            iconBgClass="bg-purple-600"
            action={
              // Seletor de Período — fica aqui (não na description bar)
              // porque é um controle de dado, não um status passivo —
              // precisa estar visualmente colado ao conteúdo que ele filtra.
              <div className="flex items-center bg-purple-50/60 border border-purple-100 rounded-lg p-0.5 gap-0.5 self-center">
                {PERIODS.map((p) => (
                  <button
                    key={p.key}
                    onClick={() => setPeriod(p.key)}
                    className={cn(
                      "px-2.5 py-1 rounded-md text-xs font-bold transition-all focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-purple-500",
                      period === p.key
                        ? "bg-white text-purple-700 shadow-xs"
                        : "text-gray-500 hover:text-gray-700",
                    )}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            }
          />

          {/* Grid de KPIs Padronizados — 1 coluna no mobile (igual ao Dashboard):
              os valores aqui são moeda formatada (ex.: "R$ 45.678,90"), mais
              longos que os inteiros do Dashboard, então precisam de mais
              largura por card em telas estreitas, não menos. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {overviewKPIs.map((kpi) => {
              const s = KPI_COLORS[kpi.color];
              const Icon = kpi.icon;
              return (
                <div
                  key={kpi.title}
                  className={cn(
                    "relative bg-white rounded-xl border p-3 shadow-xs transition-all duration-200 flex flex-col justify-between overflow-hidden group hover:-translate-y-0.5",
                    s.border,
                    s.hoverGlow,
                  )}
                >
                  {/* Accent Top Bar */}
                  <div
                    className={cn(
                      "absolute top-0 left-0 right-0 h-[3px]",
                      s.iconBg,
                    )}
                  />

                  <div className="flex items-start justify-between pt-0.5">
                    <div className="flex flex-col min-w-0 pr-1">
                      <div className="flex items-center gap-1 mb-1">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest truncate">
                          {kpi.title}
                        </p>
                        {kpi.tooltip && (
                          <Tooltip content={kpi.tooltip} position="top">
                            <Info className="h-3 w-3 text-gray-400 hover:text-purple-600 transition-colors cursor-help shrink-0" />
                          </Tooltip>
                        )}
                      </div>
                      <h3
                        className={cn(
                          "text-xl sm:text-2xl font-black tracking-tight leading-none tabular-nums",
                          s.text,
                        )}
                      >
                        {kpi.value}
                      </h3>
                    </div>
                    <div
                      className={cn(
                        "w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-transform duration-200 group-hover:scale-110 shadow-xs",
                        s.iconBg,
                        s.iconText,
                      )}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                  </div>

                  <div
                    className={cn(
                      "flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-lg mt-2.5 truncate",
                      s.subBg,
                      s.subText,
                    )}
                  >
                    <BarChart3 className="h-3 w-3 shrink-0" />
                    <span className="truncate">{kpi.sub}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ========================================================================= */}
        {/* BANDA 2: Performance por Canal (Paid & Local Media)                       */}
        {/* ========================================================================= */}
        <div className="shrink-0 flex flex-col bg-white rounded-xl border border-purple-200 p-3 shadow-xs">
          <SectionHeader
            icon={<BarChart3 className="h-5 w-5" />}
            title={t("Performance por Canal")}
            subtitle={t("Mídia Paga & Presença Local")}
            iconBgClass="bg-indigo-600"
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {/* 1. Facebook Ads */}
            <div className="bg-white border border-[#1877F2]/20 rounded-xl p-3 hover:border-[#1877F2]/40 hover:shadow-md transition-all flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-2.5 pb-2 border-b border-[#1877F2]/10">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-[#1877F2]/10 border border-[#1877F2]/20 flex items-center justify-center shrink-0">
                      <svg
                        viewBox="0 0 24 24"
                        className="h-4 w-4"
                        fill="#1877F2"
                      >
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-xs font-black text-gray-900 leading-none">
                        Facebook Ads
                      </h4>
                      <p className="text-[9px] text-gray-400 font-bold uppercase tracking-wider mt-0.5">
                        {t("{count} campanha(s)", {
                          count: stats.facebook.count,
                        })}
                      </p>
                    </div>
                  </div>
                  <Badge
                    variant="default"
                    size="sm"
                    className="bg-[#1877F2]/10 text-[#1877F2] border-[#1877F2]/20 text-[9px] font-black tracking-wider"
                  >
                    {stats.facebook.roi > 0
                      ? `${stats.facebook.roi.toFixed(1)}x ROI`
                      : t("Meta")}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-1.5">
                  <div className="bg-blue-50/60 border border-blue-100/60 rounded-lg p-2">
                    <div className="flex items-center gap-1 mb-0.5">
                      <DollarSign className="h-2.5 w-2.5 text-blue-500" />
                      <span className="text-[8px] font-black text-blue-600 uppercase tracking-wider">
                        {t("Investido")}
                      </span>
                    </div>
                    <p className="text-xs font-black text-blue-900 tabular-nums">
                      {new Intl.NumberFormat("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      }).format(stats.facebook.spent)}
                    </p>
                  </div>
                  <div className="bg-purple-50/60 border border-purple-100/60 rounded-lg p-2">
                    <div className="flex items-center gap-1 mb-0.5">
                      <Users className="h-2.5 w-2.5 text-purple-500" />
                      <span className="text-[8px] font-black text-purple-600 uppercase tracking-wider">
                        {t("Leads")}
                      </span>
                    </div>
                    <p className="text-xs font-black text-purple-900 tabular-nums">
                      {fmt(stats.facebook.leads)}
                    </p>
                  </div>
                  <div className="bg-rose-50/60 border border-rose-100/60 rounded-lg p-2">
                    <div className="flex items-center gap-1 mb-0.5">
                      <Eye className="h-2.5 w-2.5 text-rose-500" />
                      <span className="text-[8px] font-black text-rose-600 uppercase tracking-wider">
                        {t("Impressões")}
                      </span>
                    </div>
                    <p className="text-xs font-black text-rose-900 tabular-nums">
                      {fmt(stats.facebook.impressions)}
                    </p>
                  </div>
                  <div className="bg-amber-50/60 border border-amber-100/60 rounded-lg p-2">
                    <div className="flex items-center gap-1 mb-0.5">
                      <MousePointer2 className="h-2.5 w-2.5 text-amber-500" />
                      <span className="text-[8px] font-black text-amber-600 uppercase tracking-wider">
                        {t("Cliques")}
                      </span>
                    </div>
                    <p className="text-xs font-black text-amber-900 tabular-nums">
                      {fmt(stats.facebook.clicks)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 2. Instagram Ads */}
            <div className="bg-white border border-[#E4405F]/20 rounded-xl p-3 hover:border-[#E4405F]/40 hover:shadow-md transition-all flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-2.5 pb-2 border-b border-[#E4405F]/10">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-[#E4405F]/10 border border-[#E4405F]/20 flex items-center justify-center shrink-0">
                      <svg
                        viewBox="0 0 24 24"
                        className="h-4 w-4"
                        fill="#E4405F"
                      >
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-xs font-black text-gray-900 leading-none">
                        Instagram Ads
                      </h4>
                      <p className="text-[9px] text-gray-400 font-bold uppercase tracking-wider mt-0.5">
                        {t("{count} campanha(s)", {
                          count: stats.instagram.count,
                        })}
                      </p>
                    </div>
                  </div>
                  <Badge
                    variant="default"
                    size="sm"
                    className="bg-[#E4405F]/10 text-[#E4405F] border-[#E4405F]/20 text-[9px] font-black tracking-wider"
                  >
                    {stats.instagram.roi > 0
                      ? `${stats.instagram.roi.toFixed(1)}x ROI`
                      : t("Reels/Feed")}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-1.5">
                  <div className="bg-blue-50/60 border border-blue-100/60 rounded-lg p-2">
                    <div className="flex items-center gap-1 mb-0.5">
                      <DollarSign className="h-2.5 w-2.5 text-blue-500" />
                      <span className="text-[8px] font-black text-blue-600 uppercase tracking-wider">
                        {t("Investido")}
                      </span>
                    </div>
                    <p className="text-xs font-black text-blue-900 tabular-nums">
                      {new Intl.NumberFormat("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      }).format(stats.instagram.spent)}
                    </p>
                  </div>
                  <div className="bg-purple-50/60 border border-purple-100/60 rounded-lg p-2">
                    <div className="flex items-center gap-1 mb-0.5">
                      <Users className="h-2.5 w-2.5 text-purple-500" />
                      <span className="text-[8px] font-black text-purple-600 uppercase tracking-wider">
                        {t("Leads")}
                      </span>
                    </div>
                    <p className="text-xs font-black text-purple-900 tabular-nums">
                      {fmt(stats.instagram.leads)}
                    </p>
                  </div>
                  <div className="bg-rose-50/60 border border-rose-100/60 rounded-lg p-2">
                    <div className="flex items-center gap-1 mb-0.5">
                      <Eye className="h-2.5 w-2.5 text-rose-500" />
                      <span className="text-[8px] font-black text-rose-600 uppercase tracking-wider">
                        {t("Impressões")}
                      </span>
                    </div>
                    <p className="text-xs font-black text-rose-900 tabular-nums">
                      {fmt(stats.instagram.impressions)}
                    </p>
                  </div>
                  <div className="bg-amber-50/60 border border-amber-100/60 rounded-lg p-2">
                    <div className="flex items-center gap-1 mb-0.5">
                      <MousePointer2 className="h-2.5 w-2.5 text-amber-500" />
                      <span className="text-[8px] font-black text-amber-600 uppercase tracking-wider">
                        {t("Cliques")}
                      </span>
                    </div>
                    <p className="text-xs font-black text-amber-900 tabular-nums">
                      {fmt(stats.instagram.clicks)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 3. Google Ads */}
            <div className="bg-white border border-[#4285F4]/20 rounded-xl p-3 hover:border-[#4285F4]/40 hover:shadow-md transition-all flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-2.5 pb-2 border-b border-[#4285F4]/10">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-[#4285F4]/10 border border-[#4285F4]/20 flex items-center justify-center shrink-0">
                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none">
                        <path
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          fill="#4285F4"
                        />
                        <path
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          fill="#34A853"
                        />
                        <path
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                          fill="#FBBC05"
                        />
                        <path
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          fill="#EA4335"
                        />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-xs font-black text-gray-900 leading-none">
                        Google Ads
                      </h4>
                      <p className="text-[9px] text-gray-400 font-bold uppercase tracking-wider mt-0.5">
                        {t("{count} campanha(s)", {
                          count: stats.google.count,
                        })}
                      </p>
                    </div>
                  </div>
                  <Badge
                    variant="default"
                    size="sm"
                    className="bg-[#4285F4]/10 text-[#4285F4] border-[#4285F4]/20 text-[9px] font-black tracking-wider"
                  >
                    {stats.google.roi > 0
                      ? `${stats.google.roi.toFixed(1)}x ROI`
                      : t("Search")}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-1.5">
                  <div className="bg-blue-50/60 border border-blue-100/60 rounded-lg p-2">
                    <div className="flex items-center gap-1 mb-0.5">
                      <DollarSign className="h-2.5 w-2.5 text-blue-500" />
                      <span className="text-[8px] font-black text-blue-600 uppercase tracking-wider">
                        {t("Investido")}
                      </span>
                    </div>
                    <p className="text-xs font-black text-blue-900 tabular-nums">
                      {new Intl.NumberFormat("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      }).format(stats.google.spent)}
                    </p>
                  </div>
                  <div className="bg-emerald-50/60 border border-emerald-100/60 rounded-lg p-2">
                    <div className="flex items-center gap-1 mb-0.5">
                      <Target className="h-2.5 w-2.5 text-emerald-500" />
                      <span className="text-[8px] font-black text-emerald-600 uppercase tracking-wider">
                        {t("Conversões")}
                      </span>
                    </div>
                    <p className="text-xs font-black text-emerald-900 tabular-nums">
                      {fmt(stats.google.leads)}
                    </p>
                  </div>
                  <div className="bg-rose-50/60 border border-rose-100/60 rounded-lg p-2">
                    <div className="flex items-center gap-1 mb-0.5">
                      <Eye className="h-2.5 w-2.5 text-rose-500" />
                      <span className="text-[8px] font-black text-rose-600 uppercase tracking-wider">
                        {t("Impressões")}
                      </span>
                    </div>
                    <p className="text-xs font-black text-rose-900 tabular-nums">
                      {fmt(stats.google.impressions)}
                    </p>
                  </div>
                  <div className="bg-amber-50/60 border border-amber-100/60 rounded-lg p-2">
                    <div className="flex items-center gap-1 mb-0.5">
                      <MousePointer2 className="h-2.5 w-2.5 text-amber-500" />
                      <span className="text-[8px] font-black text-amber-600 uppercase tracking-wider">
                        {t("Cliques")}
                      </span>
                    </div>
                    <p className="text-xs font-black text-amber-900 tabular-nums">
                      {fmt(stats.google.clicks)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 4. Google Meu Negócio */}
            <div className="bg-white border border-green-200 rounded-xl p-3 hover:border-green-300 hover:shadow-md transition-all flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-2.5 pb-2 border-b border-green-100">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-green-50 border border-green-200 flex items-center justify-center shrink-0">
                      <MapPin className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <h4 className="text-xs font-black text-gray-900 leading-none">
                        {t("Meu Negócio")}
                      </h4>
                      <p className="text-[9px] text-gray-400 font-bold uppercase tracking-wider mt-0.5">
                        {gmbData
                          ? t("{count} local(is)", {
                              count: gmbData.locations_count,
                            })
                          : t("Presença Local")}
                      </p>
                    </div>
                  </div>
                  <Badge
                    variant="default"
                    size="sm"
                    className="bg-green-50 text-green-700 border-green-200 text-[9px] font-black tracking-wider"
                  >
                    {t("Local Search")}
                  </Badge>
                </div>

                {gmbLoading ? (
                  <div className="grid grid-cols-2 gap-1.5 animate-pulse">
                    {[0, 1, 2, 3].map((i) => (
                      <div key={i} className="bg-gray-100 rounded-lg h-12" />
                    ))}
                  </div>
                ) : gmbData ? (
                  <div className="grid grid-cols-2 gap-1.5">
                    <div className="bg-blue-50/60 border border-blue-100/60 rounded-lg p-2">
                      <div className="flex items-center gap-1 mb-0.5">
                        <Search className="h-2.5 w-2.5 text-blue-500" />
                        <span className="text-[8px] font-black text-blue-600 uppercase tracking-wider">
                          {t("Buscas")}
                        </span>
                      </div>
                      <p className="text-xs font-black text-blue-900 tabular-nums">
                        {fmt(gmbData.search_impressions)}
                      </p>
                    </div>
                    <div className="bg-purple-50/60 border border-purple-100/60 rounded-lg p-2">
                      <div className="flex items-center gap-1 mb-0.5">
                        <MapPin className="h-2.5 w-2.5 text-purple-500" />
                        <span className="text-[8px] font-black text-purple-600 uppercase tracking-wider">
                          {t("Rotas")}
                        </span>
                      </div>
                      <p className="text-xs font-black text-purple-900 tabular-nums">
                        {fmt(gmbData.direction_requests)}
                      </p>
                    </div>
                    <div className="bg-emerald-50/60 border border-emerald-100/60 rounded-lg p-2">
                      <div className="flex items-center gap-1 mb-0.5">
                        <MousePointer2 className="h-2.5 w-2.5 text-emerald-500" />
                        <span className="text-[8px] font-black text-emerald-600 uppercase tracking-wider">
                          {t("Cliques")}
                        </span>
                      </div>
                      <p className="text-xs font-black text-emerald-900 tabular-nums">
                        {fmt(gmbData.website_clicks)}
                      </p>
                    </div>
                    <div className="bg-amber-50/60 border border-amber-100/60 rounded-lg p-2">
                      <div className="flex items-center gap-1 mb-0.5">
                        <Phone className="h-2.5 w-2.5 text-amber-500" />
                        <span className="text-[8px] font-black text-amber-600 uppercase tracking-wider">
                          {t("Ligações")}
                        </span>
                      </div>
                      <p className="text-xs font-black text-amber-900 tabular-nums">
                        {fmt(gmbData.phone_calls)}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    <p className="text-[11px] text-gray-400 font-medium">
                      {t("Dados locais indisponíveis")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* BANDA 3: Ranking de Campanhas & Demografia LinkedIn                        */}
        {/* ========================================================================= */}
        <div
          className={cn(
            "grid gap-2",
            linkedinCampaignsList.length > 0
              ? "grid-cols-1 lg:grid-cols-12"
              : "grid-cols-1",
          )}
        >
          {/* Card 1: Ranking de Performance de Campanhas */}
          <div
            className={cn(
              "flex flex-col bg-white rounded-xl border border-purple-200 p-3 shadow-xs",
              linkedinCampaignsList.length > 0 ? "lg:col-span-7" : "w-full",
            )}
          >
            <SectionHeader
              icon={<Award className="h-5 w-5" />}
              title={t("Ranking de Campanhas")}
              subtitle={t("Top campanhas por retorno real")}
              iconBgClass="bg-purple-600"
              action={
                <Badge
                  variant="purple"
                  size="sm"
                  className="font-bold uppercase tracking-wider"
                >
                  {t("{count} Campanhas", { count: campaigns.length })}
                </Badge>
              }
            />

            <div className="divide-y divide-purple-100">
              {campaigns.length === 0 ? (
                <div className="py-8 text-center bg-purple-50/20 rounded-xl border border-dashed border-purple-200">
                  <Target className="h-8 w-8 text-purple-400 mx-auto mb-2 opacity-60" />
                  <p className="text-xs font-bold text-gray-700">
                    {t("Nenhuma campanha ativa no período")}
                  </p>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    {t(
                      "Altere o filtro de período acima para visualizar dados históricos.",
                    )}
                  </p>
                </div>
              ) : (
                campaigns
                  .slice()
                  .sort((a, b) => (b.roi || 0) - (a.roi || 0))
                  .slice(0, 5)
                  .map((campaign, idx) => {
                    const medalStyles = [
                      "bg-amber-100 text-amber-800 border-amber-300 shadow-xs",
                      "bg-slate-100 text-slate-700 border-slate-300 shadow-xs",
                      "bg-orange-100 text-orange-800 border-orange-300 shadow-xs",
                    ];
                    const badgeClass =
                      idx < 3
                        ? medalStyles[idx]
                        : "bg-gray-100 text-gray-500 border-gray-200";

                    return (
                      <div
                        key={campaign.id}
                        className="group flex items-center justify-between gap-3 py-2.5 px-2 hover:bg-purple-50/30 rounded-lg transition-colors"
                      >
                        {/* Posição + Identificação */}
                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                          <div
                            className={cn(
                              "w-7 h-7 rounded-lg border flex items-center justify-center text-[11px] font-black shrink-0",
                              badgeClass,
                            )}
                          >
                            #{idx + 1}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-bold text-gray-900 truncate group-hover:text-purple-700 transition-colors">
                              {campaign.name}
                            </p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="flex items-center gap-1 text-[9px] font-bold text-gray-400 uppercase tracking-wider">
                                <PlatformIcon
                                  platform={campaign.platform}
                                  className="h-3 w-3"
                                />
                                {getPlatformName(campaign.platform)}
                              </span>
                              <span className="text-[9px] text-gray-300 font-bold">
                                ·
                              </span>
                              <span className="text-[9px] font-bold text-gray-500">
                                {t("Investido")}:{" "}
                                {new Intl.NumberFormat("pt-BR", {
                                  style: "currency",
                                  currency: "BRL",
                                }).format(campaign.spent || 0)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Métricas Alinhadas */}
                        <div className="flex items-center gap-4 shrink-0 text-right">
                          <div className="hidden sm:block">
                            <p className="text-[8px] font-black text-gray-400 uppercase tracking-wider">
                              {t("Leads")}
                            </p>
                            <p className="text-xs font-black text-gray-900 tabular-nums">
                              {campaign.conversions}
                            </p>
                          </div>

                          <div className="hidden sm:block">
                            <p className="text-[8px] font-black text-gray-400 uppercase tracking-wider">
                              {t("Custo / Lead")}
                            </p>
                            <p className="text-xs font-black text-purple-700 tabular-nums">
                              {new Intl.NumberFormat("pt-BR", {
                                style: "currency",
                                currency: "BRL",
                              }).format(campaign.cost_per_lead || 0)}
                            </p>
                          </div>

                          <div>
                            <span className="inline-flex items-center gap-0.5 px-2 py-1 rounded-lg bg-purple-50 border border-purple-200 text-purple-700 text-xs font-black tabular-nums shadow-xs">
                              <Zap className="h-3 w-3 text-purple-500" />
                              {campaign.roi.toFixed(1)}x
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })
              )}
            </div>
          </div>

          {/* Card 2: LinkedIn Demographics (Renderizado apenas se houver campanhas de LinkedIn) */}
          {linkedinCampaignsList.length > 0 && selectedLinkedInCamp && (
            <div className="flex flex-col bg-white rounded-xl border border-purple-200 p-3 shadow-xs lg:col-span-5 h-full overflow-hidden">
              <SectionHeader
                icon={<Users className="h-5 w-5" />}
                title={t("Demografia: LinkedIn")}
                subtitle={t("Perfil profissional impactado")}
                iconBgClass="bg-[#0e76a8]"
                action={
                  linkedinCampaignsList.length > 1 && (
                    <select
                      value={selectedLinkedInCampId}
                      onChange={(e) =>
                        setSelectedLinkedInCampId(e.target.value)
                      }
                      title={
                        linkedinCampaignsList.find(
                          (c) => c.id === selectedLinkedInCampId,
                        )?.name
                      }
                      className="text-xs font-bold text-gray-700 border-purple-200 rounded-lg shadow-xs focus:border-purple-500 focus:ring-purple-500 bg-purple-50/50 py-1 px-2 max-w-[160px] truncate"
                    >
                      {linkedinCampaignsList.map((c) => (
                        <option key={c.id} value={c.id} title={c.name}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  )
                }
              />

              <div className="flex-1 min-h-0 overflow-y-auto mt-1 pr-1 custom-scrollbar">
                <LinkedInDemographics campaignId={selectedLinkedInCamp.id} />
              </div>
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default AdsAnalytics;
